import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RootState } from '@/store';
import { ROUTES } from '@/utils/constants';
import { ordersApi } from '@/services/api';
import { formatCurrency } from '@/utils/formatters';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { staggerContainer, staggerItem } from '@/utils/animations';
import {
  ChartBarIcon,
  ShoppingBagIcon,
  BuildingStorefrontIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  PlusIcon,
  UserIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface SellerStats {
  total_products: number;
  total_orders: number;
  total_revenue: number;
  pending_orders: number;
  low_stock_products: number;
  revenue_growth: number;
  orders_growth: number;
  products_growth: number;
}

interface RecentOrder {
  id: number;
  order_number: string;
  buyer_name: string;
  total_amount: number;
  order_status: string;
  created_at: string;
}

interface ShopInfo {
  id: number;
  name: string;
  description: string;
  is_public: boolean;
  admin_rating?: number;
}

const SellerDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [stats, setStats] = useState<SellerStats | null>(null);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [shopInfo, setShopInfo] = useState<ShopInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch real data from API
      const dashboardResponse = await ordersApi.getSellerDashboard();
      const dashboardData = dashboardResponse.data;

      setStats({
        total_products: dashboardData.total_products,
        total_orders: dashboardData.total_orders,
        total_revenue: dashboardData.total_revenue,
        pending_orders: dashboardData.pending_orders,
        low_stock_products: dashboardData.low_stock_products,
        revenue_growth: dashboardData.revenue_growth,
        orders_growth: dashboardData.orders_growth,
        products_growth: dashboardData.products_growth,
      });

      setShopInfo(dashboardData.shop_info);

      // Fetch recent orders
      const recentOrdersResponse = await ordersApi.getSellerRecentOrders(5);
      setRecentOrders(recentOrdersResponse.data);

    } catch (error: any) {
      // Handle connection errors gracefully
      if (error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('Network Error')) {
        setError('Unable to connect to server. Please ensure the backend is running.');
        console.error('Connection error:', error);
        return;
      }
      
      // If shop not found, redirect to shop creation
      if (error.detail && (error.detail.includes('Shop not found') || error.response?.status === 404)) {
        // Use navigate instead of window.location for better UX
        navigate('/dashboard/seller/shop/create', { replace: true });
        return;
      }
      
      setError(error.detail || error.message || 'Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-warning-100 dark:bg-warning-900/20 text-warning-700 dark:text-warning-400 border border-warning-200 dark:border-warning-800';
      case 'confirmed':
        return 'bg-info-100 dark:bg-info-900/20 text-info-700 dark:text-info-400 border border-info-200 dark:border-info-800';
      case 'shipped':
        return 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border border-primary-200 dark:border-primary-800';
      case 'delivered':
        return 'bg-success-100 dark:bg-success-900/20 text-success-700 dark:text-success-400 border border-success-200 dark:border-success-800';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-700';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 dark:border-primary-900 border-t-primary-600 dark:border-t-primary-400 mx-auto"></div>
          <p className="mt-6 text-gray-600 dark:text-gray-400 text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div initial="initial" animate="animate" variants={staggerContainer}>
      {/* Header */}
      <motion.div variants={staggerItem} className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">Seller Dashboard</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Welcome back, {user?.first_name}! Manage your shop and track your sales.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              size="md"
              onClick={() => navigate('/dashboard/buyer')}
              leftIcon={<UserIcon className="h-5 w-5" />}
            >
              Switch to Buyer
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={fetchDashboardData}
              leftIcon={<ArrowPathIcon className="h-5 w-5" />}
            >
              Refresh Data
            </Button>
            <Button
              size="md"
              onClick={() => navigate('/dashboard/seller/products/add')}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              Add Product
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Error Message */}
      {error && (
        <motion.div variants={staggerItem} className="p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg mb-6">
          <p className="text-sm text-error-600 dark:text-error-400">{error}</p>
        </motion.div>
      )}

      {/* Shop Status Alert */}
      {shopInfo && !shopInfo.is_public && (
        <motion.div variants={staggerItem} className="p-4 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg mb-6">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-warning-900 dark:text-warning-300">Shop Pending Approval</p>
              <p className="text-sm text-warning-700 dark:text-warning-400 mt-1">
                Your shop is currently under review. You can still add products, but they won't be visible to customers until approved.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats Grid */}
      {stats && (
        <motion.div variants={staggerItem} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Products */}
          <div className="group bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6 hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Products</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {stats.total_products.toLocaleString()}
                </p>
                <div className="flex items-center mt-3">
                  {stats.products_growth >= 0 ? (
                    <ArrowUpIcon className="h-4 w-4 text-success-500 dark:text-success-400 mr-1" />
                  ) : (
                    <ArrowDownIcon className="h-4 w-4 text-error-500 dark:text-error-400 mr-1" />
                  )}
                  <span className={cn('text-sm font-medium', stats.products_growth >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400')}>
                    {Math.abs(stats.products_growth)}% from last month
                  </span>
                </div>
              </div>
              <div className="p-3 bg-primary-100 dark:bg-primary-900/20 rounded-xl group-hover:scale-110 transition-transform duration-200">
                <ChartBarIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
          </div>

          {/* Total Orders */}
          <div className="group bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6 hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Orders</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {stats.total_orders.toLocaleString()}
                </p>
                <div className="flex items-center mt-3">
                  {stats.orders_growth >= 0 ? (
                    <ArrowUpIcon className="h-4 w-4 text-success-500 dark:text-success-400 mr-1" />
                  ) : (
                    <ArrowDownIcon className="h-4 w-4 text-error-500 dark:text-error-400 mr-1" />
                  )}
                  <span className={cn('text-sm font-medium', stats.orders_growth >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400')}>
                    {Math.abs(stats.orders_growth)}% from last month
                  </span>
                </div>
              </div>
              <div className="p-3 bg-success-100 dark:bg-success-900/20 rounded-xl group-hover:scale-110 transition-transform duration-200">
                <ShoppingBagIcon className="h-7 w-7 text-success-600 dark:text-success-400" />
              </div>
            </div>
          </div>

          {/* Total Revenue */}
          <div className="group bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6 hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Revenue</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  ${stats.total_revenue.toLocaleString()}
                </p>
                <div className="flex items-center mt-3">
                  {stats.revenue_growth >= 0 ? (
                    <ArrowUpIcon className="h-4 w-4 text-success-500 dark:text-success-400 mr-1" />
                  ) : (
                    <ArrowDownIcon className="h-4 w-4 text-error-500 dark:text-error-400 mr-1" />
                  )}
                  <span className={cn('text-sm font-medium', stats.revenue_growth >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400')}>
                    {Math.abs(stats.revenue_growth)}% from last month
                  </span>
                </div>
              </div>
              <div className="p-3 bg-accent-100 dark:bg-accent-900/20 rounded-xl group-hover:scale-110 transition-transform duration-200">
                <CurrencyDollarIcon className="h-7 w-7 text-accent-600 dark:text-accent-400" />
              </div>
            </div>
          </div>

          {/* Shop Rating */}
          <div className="group bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6 hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Shop Rating</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {shopInfo?.admin_rating || 0}/5
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                  {stats.pending_orders} pending orders
                </p>
              </div>
              <div className="p-3 bg-info-100 dark:bg-info-900/20 rounded-xl group-hover:scale-110 transition-transform duration-200">
                <BuildingStorefrontIcon className="h-7 w-7 text-info-600 dark:text-info-400" />
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Quick Actions & Alerts */}
      <motion.div variants={staggerItem} className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Button
                variant="outline"
                size="lg"
                className="h-20 flex-col gap-2"
                onClick={() => navigate(ROUTES.PRODUCT_MANAGEMENT)}
              >
                <ChartBarIcon className="h-6 w-6" />
                Manage Products
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="h-20 flex-col gap-2"
                onClick={() => navigate(ROUTES.INVENTORY)}
              >
                <ChartBarIcon className="h-6 w-6" />
                Inventory
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="h-20 flex-col gap-2"
                onClick={() => navigate(ROUTES.SELLER_ORDERS)}
              >
                <ShoppingBagIcon className="h-6 w-6" />
                Orders
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="h-20 flex-col gap-2"
                onClick={() => navigate(ROUTES.SHOP_MANAGEMENT)}
              >
                <BuildingStorefrontIcon className="h-6 w-6" />
                Shop Settings
              </Button>
            </div>
          </div>
        </div>

        {/* System Alerts */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Alerts</h2>
            <div className="space-y-3">
              {stats?.pending_orders > 0 && (
                <div className="flex items-center gap-3 p-3 bg-warning-50 dark:bg-warning-900/10 border border-warning-200 dark:border-warning-800 rounded-lg">
                  <ClockIcon className="h-5 w-5 text-warning-600 dark:text-warning-400" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{stats.pending_orders} Pending Orders</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Requires attention</p>
                  </div>
                </div>
              )}

              {stats?.low_stock_products > 0 && (
                <div className="flex items-center gap-3 p-3 bg-error-50 dark:bg-error-900/10 border border-error-200 dark:border-error-800 rounded-lg">
                  <ExclamationTriangleIcon className="h-5 w-5 text-error-600 dark:text-error-400" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{stats.low_stock_products} Low Stock Items</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Restock needed</p>
                  </div>
                </div>
              )}

              {shopInfo && !shopInfo.is_public && (
                <div className="flex items-center gap-3 p-3 bg-info-50 dark:bg-info-900/10 border border-info-200 dark:border-info-800 rounded-lg">
                  <BuildingStorefrontIcon className="h-5 w-5 text-info-600 dark:text-info-400" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Shop Pending Approval</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Awaiting admin review</p>
                  </div>
                </div>
              )}

              {(!stats?.pending_orders && !stats?.low_stock_products && shopInfo?.is_public) && (
                <div className="flex items-center gap-3 p-3 bg-success-50 dark:bg-success-900/10 border border-success-200 dark:border-success-800 rounded-lg">
                  <CheckCircleIcon className="h-5 w-5 text-success-600 dark:text-success-400" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">All Good!</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">No pending items</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Recent Orders */}
      <motion.div variants={staggerItem} className="bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Recent Orders</h2>
          <Button
            variant="outline"
            onClick={() => navigate(ROUTES.SELLER_ORDERS)}
          >
            View All Orders
          </Button>
        </div>

        <div className="space-y-3">
          {recentOrders.map((order) => (
            <div
              key={order.id}
              className="flex items-center justify-between p-4 border border-gray-200 dark:border-dark-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors duration-200"
            >
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-dark-700 rounded-lg flex items-center justify-center">
                    <ShoppingBagIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Order #{order.order_number}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Buyer: {order.buyer_name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {new Date(order.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(order.total_amount)}
                  </p>
                  <span className={cn('px-3 py-1 rounded-full text-xs font-medium', getStatusColor(order.order_status))}>
                    {order.order_status.charAt(0).toUpperCase() + order.order_status.slice(1)}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`${ROUTES.SELLER_ORDERS}/${order.id}`)}
                >
                  <EyeIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          {recentOrders.length === 0 && (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center">
                <ShoppingBagIcon className="h-10 w-10 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No orders yet</h3>
              <p className="text-gray-600 dark:text-gray-400">Start by adding products to your shop.</p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SellerDashboard;
